import type { Appointment, AvailabilityBlock, AvailabilityStatus } from '@secretario/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'wouter';
import {
  createAvailabilityBlock,
  deleteAvailabilityBlock,
  getAgendaSettings,
  getAppointments,
  getAvailabilityBlocks,
  updateAgendaSettings,
  updateAvailabilityBlock,
} from '../lib/api/personal.js';
import './ported/agenda.css';
import { BackIcon } from '../components/chrome/BackIcon.js';
import { IconClockPlus, PageActions } from '../components/chrome/icons.js';

// status de disponibilidade (blocos da Agenda Oficial)
const AV: Record<AvailabilityStatus, { label: string; c: string }> = {
  available: { label: 'Disponível', c: '#3BB980' },
  busy: { label: 'Ocupado', c: '#C4564C' },
};

const WD_SHORT = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const WD_LABEL = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const ALL_DAY = { start: '00:00', end: '23:59' };

function dateKey(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function todayKey() {
  return dateKey(new Date());
}
const isAllDay = (b: { startTime: string; endTime: string }) =>
  b.startTime === '00:00' && b.endTime === '23:59';

const sc = (color: string) => ({ ['--sc' as string]: color }) as React.CSSProperties;

// ── Editor (dia específico OU rotina semanal) ──
type EditorCtx = { kind: 'date'; key: string } | { kind: 'week' };
function Editor({
  ctx,
  block,
  onClose,
  onSaved,
}: {
  ctx: EditorCtx;
  block: AvailabilityBlock | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState<AvailabilityStatus>(block?.status ?? 'available');
  const [allDay, setAllDay] = useState(block ? isAllDay(block) : true);
  const [start, setStart] = useState(block?.startTime ?? '09:00');
  const [end, setEnd] = useState(block?.endTime ?? '18:00');
  const [weekdays, setWeekdays] = useState<number[]>(
    block?.weekday != null ? [block.weekday] : [1, 2, 3, 4, 5],
  );
  const [saving, setSaving] = useState(false);

  const dateObj = ctx.kind === 'date' ? new Date(ctx.key + 'T12:00:00') : null;
  const heading =
    ctx.kind === 'date' && dateObj
      ? dateObj.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })
      : block
        ? 'Editar horário fixo'
        : 'Novo horário fixo';

  function toggleWd(n: number) {
    setWeekdays((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n].sort()));
  }

  async function save() {
    if (saving) return;
    if (ctx.kind === 'week' && weekdays.length === 0) return;
    setSaving(true);
    const base = {
      status,
      startTime: allDay ? ALL_DAY.start : start,
      endTime: allDay ? ALL_DAY.end : end,
    };
    try {
      if (ctx.kind === 'date') {
        if (block)
          await updateAvailabilityBlock(block.id, { ...base, date: ctx.key, weekday: null });
        else await createAvailabilityBlock({ ...base, date: ctx.key });
      } else if (block) {
        await updateAvailabilityBlock(block.id, { ...base, weekday: weekdays[0] ?? 1, date: null });
      } else {
        // um bloco por dia da semana selecionado
        for (const w of weekdays) await createAvailabilityBlock({ ...base, weekday: w });
      }
      onSaved();
    } catch {
      /* silencioso */
    } finally {
      setSaving(false);
    }
  }
  async function clear() {
    if (!block || saving) return;
    setSaving(true);
    try {
      await deleteAvailabilityBlock(block.id);
      onSaved();
    } catch {
      /* ok */
    } finally {
      setSaving(false);
    }
  }

  return createPortal(
    <div className="ag-sheetwrap" onClick={onClose}>
      <div className="ag-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="ag-grip" />
        <div className="ag-sheet-h">{heading}</div>

        {ctx.kind === 'week' && (
          <>
            <div className="ag-flbl">Em quais dias</div>
            <div className="ag-wds">
              {WD_SHORT.map((d, i) => (
                <button
                  key={i}
                  type="button"
                  className={`ag-wd${weekdays.includes(i) ? ' on' : ''}`}
                  onClick={() => toggleWd(i)}
                  disabled={!!block}
                >
                  {d}
                </button>
              ))}
            </div>
          </>
        )}

        <div className="ag-flbl">Você fica</div>
        <div className="ag-stat">
          {(['available', 'busy'] as AvailabilityStatus[]).map((k) => (
            <button
              key={k}
              type="button"
              className={`ag-statbtn${status === k ? ' on' : ''}`}
              style={sc(AV[k].c)}
              onClick={() => setStatus(k)}
            >
              <i className="ag-dot" aria-hidden="true" />
              {AV[k].label}
            </button>
          ))}
        </div>

        <button
          type="button"
          className={`ag-allday${allDay ? ' on' : ''}`}
          onClick={() => setAllDay((v) => !v)}
        >
          <span className="ag-knob" /> Dia todo
        </button>
        {!allDay && (
          <div className="ag-times">
            <label>
              Das <input type="time" value={start} onChange={(e) => setStart(e.target.value)} />
            </label>
            <label>
              até <input type="time" value={end} onChange={(e) => setEnd(e.target.value)} />
            </label>
          </div>
        )}

        <div className="ag-sheet-act">
          {block && (
            <button className="ag-clear" onClick={() => void clear()} disabled={saving}>
              Limpar
            </button>
          )}
          <button className="ag-savebtn" onClick={() => void save()} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
        <button className="ag-cancel" onClick={onClose}>
          Cancelar
        </button>
      </div>
    </div>,
    document.body,
  );
}

export function AgendaRotinaPage() {
  const qc = useQueryClient();
  const [monthCursor, setMonthCursor] = useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [editor, setEditor] = useState<{ ctx: EditorCtx; block: AvailabilityBlock | null } | null>(
    null,
  );

  const range = useMemo(() => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    return { from: from.toISOString(), to: new Date(from.getTime() + 60 * 86400000).toISOString() };
  }, []);
  const apptQ = useQuery({
    queryKey: ['agenda-appointments', range.from],
    queryFn: () => getAppointments(range.from, range.to),
    staleTime: 20_000,
  });
  const blocksQ = useQuery({
    queryKey: ['agenda-blocks'],
    queryFn: getAvailabilityBlocks,
    staleTime: 20_000,
  });
  const settingsQ = useQuery({
    queryKey: ['agenda-settings'],
    queryFn: getAgendaSettings,
    staleTime: 30_000,
  });
  // Garante que a Agenda Oficial esteja LIGADA (senão o secretário não consulta disponibilidade).
  useEffect(() => {
    if (settingsQ.data && settingsQ.data.enabled === false) {
      updateAgendaSettings({ enabled: true })
        .then(() => qc.invalidateQueries({ queryKey: ['agenda-settings'] }))
        .catch(() => {});
    }
  }, [settingsQ.data, qc]);

  const blocks = blocksQ.data ?? [];
  const dateBlocks = useMemo(() => {
    const m = new Map<string, AvailabilityBlock>();
    for (const b of blocks) if (b.date) m.set(b.date, b);
    return m;
  }, [blocks]);
  const weeklyBlocks = useMemo(
    () =>
      blocks
        .filter((b) => b.weekday != null && !b.date)
        .sort(
          (a, b) => (a.weekday ?? 0) - (b.weekday ?? 0) || a.startTime.localeCompare(b.startTime),
        ),
    [blocks],
  );

  const apptsByDay = useMemo(() => {
    const m = new Map<string, Appointment[]>();
    for (const a of apptQ.data ?? []) {
      if (a.status === 'cancelled') continue;
      const d = new Date(a.startsAt);
      if (Number.isNaN(d.getTime())) continue;
      const k = dateKey(d);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(a);
    }
    return m;
  }, [apptQ.data]);

  const grid = useMemo(() => {
    const y = monthCursor.getFullYear();
    const mo = monthCursor.getMonth();
    const startPad = new Date(y, mo, 1).getDay();
    const daysInMonth = new Date(y, mo + 1, 0).getDate();
    const cells: (Date | null)[] = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, mo, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [monthCursor]);

  const tk = todayKey();
  function refresh() {
    void qc.invalidateQueries({ queryKey: ['agenda-blocks'] });
    setEditor(null);
  }

  const requireApproval = settingsQ.data?.requireOwnerApproval ?? false;
  async function toggleApproval() {
    try {
      await updateAgendaSettings({ requireOwnerApproval: !requireApproval });
      await qc.invalidateQueries({ queryKey: ['agenda-settings'] });
    } catch {
      /* ok */
    }
  }

  const fmtBlockTime = (b: AvailabilityBlock) =>
    isAllDay(b) ? 'dia todo' : `${b.startTime}–${b.endTime}`;

  return (
    <div className="ag">
      <div className="ag-wrap">
        <header className="ag-hd">
          <Link href="/agenda" className="ag-back back-btn" aria-label="Voltar para a agenda">
            <BackIcon />
          </Link>
          <PageActions>
            <button
              className="icon-btn"
              aria-label="Novo horário fixo"
              onClick={() => setEditor({ ctx: { kind: 'week' }, block: null })}
            >
              <IconClockPlus />
            </button>
          </PageActions>
        </header>

        <div className="ag-kicker">Sua agenda</div>
        <h1 className="ag-h1">Quando você pode</h1>
        <p className="ag-sub">
          Toque num dia pra marcar disponível ou ocupado. Seu secretário consulta isso antes de
          combinar qualquer horário.
        </p>

        {/* Calendário */}
        <div className="ag-cal">
          <div className="ag-calhd">
            <button
              className="ag-nav"
              onClick={() => setMonthCursor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
              aria-label="Mês anterior"
            >
              ‹
            </button>
            <b>
              {MONTHS[monthCursor.getMonth()]} {monthCursor.getFullYear()}
            </b>
            <button
              className="ag-nav"
              onClick={() => setMonthCursor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
              aria-label="Próximo mês"
            >
              ›
            </button>
          </div>
          <div className="ag-wdrow">
            {WD_SHORT.map((d, i) => (
              <span key={i}>{d}</span>
            ))}
          </div>
          <div className="ag-days">
            {grid.map((d, i) => {
              if (!d) return <span key={i} className="ag-daycell empty" />;
              const k = dateKey(d);
              const block = dateBlocks.get(k);
              const hasAppt = apptsByDay.has(k);
              const isPast = k < tk;
              const isToday = k === tk;
              const c = block ? AV[block.status].c : undefined;
              return (
                <button
                  key={i}
                  className={`ag-daycell${isToday ? ' today' : ''}${isPast ? ' past' : ''}${block ? ' ruled' : ''}`}
                  style={c ? ({ ['--dc' as string]: c } as React.CSSProperties) : undefined}
                  onClick={() =>
                    !isPast && setEditor({ ctx: { kind: 'date', key: k }, block: block ?? null })
                  }
                  disabled={isPast}
                >
                  <span className="ag-daynum">{d.getDate()}</span>
                  {hasAppt && <span className="ag-appdot" />}
                </button>
              );
            })}
          </div>
          <div className="ag-legend">
            <span>
              <i style={{ background: AV.available.c }} /> Disponível
            </span>
            <span>
              <i style={{ background: AV.busy.c }} /> Ocupado
            </span>
            <span>
              <i className="ring" /> Compromisso
            </span>
          </div>
        </div>

        {/* Perguntar antes de confirmar */}
        <button
          className={`ag-approval${requireApproval ? ' on' : ''}`}
          onClick={() => void toggleApproval()}
        >
          <span className="ag-knob" />
          <span className="ag-approval-t">
            <b>Me perguntar antes de confirmar</b>
            <small>
              {requireApproval
                ? 'O secretário te consulta antes de marcar'
                : 'O secretário pode confirmar sozinho nos horários livres'}
            </small>
          </span>
        </button>

        {/* Rotina da semana */}
        <div className="ag-week">
          <div className="ag-weekhd">
            <h3>Sua rotina da semana</h3>
            <button
              className="ag-add"
              onClick={() => setEditor({ ctx: { kind: 'week' }, block: null })}
            >
              + Horário fixo
            </button>
          </div>
          {weeklyBlocks.length === 0 ? (
            <p className="ag-weekempty">
              Nenhuma rotina fixa. Ex.: “disponível de seg a sex, das 9h às 18h”.
            </p>
          ) : (
            <div className="ag-rulelist">
              {weeklyBlocks.map((b) => {
                const info = AV[b.status];
                return (
                  <button
                    key={b.id}
                    className="ag-rule"
                    style={sc(info.c)}
                    onClick={() => setEditor({ ctx: { kind: 'week' }, block: b })}
                  >
                    <span className="ag-ruledays">
                      {b.weekday != null ? WD_LABEL[b.weekday] : ''}
                    </span>
                    <span className="ag-ruleinfo">
                      <i className="ag-dot" aria-hidden="true" /> {info.label} · {fmtBlockTime(b)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editor && (
        <Editor
          ctx={editor.ctx}
          block={editor.block}
          onClose={() => setEditor(null)}
          onSaved={refresh}
        />
      )}
    </div>
  );
}
